import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  HeartIcon,
  HeartSolidIcon,
  Tooltip,
  useToaster
} from "@dealer-center/core-components";
import styled from "styled-components";
import {
  PagesLandingPageListItem,
  StyledUnorderedList
} from "@dealer-center/ddc-core-custom-components";
import { useAccountFavourites } from "@dealer-center/ddc-api-account-fetch";
import { useOutletContext } from "react-router-dom";
import type { AccountEntityExtended } from "@dealer-center/ddc-api-account-interface";
import { AccountFranchiseSelector } from "../franchise-selector";
import { useFranchiseList } from "@dealer-center/ddc-pages-franchise-management-fetch";
import type { IFranchiseSelectorData } from "@dealer-center/ddc-pages-franchise-management-interface";
import type { IDashboardProps } from "@dealer-center/ddc-pages-dashboard-interface";

const StyledCard = styled(Card)`
  .panel-title {
    width: 100%;
  }
`;
const StyledCardTitleArea = styled.div`
  display: flex;
`;

export function DashboardDealerInfoCard(props: IDashboardProps) {
  const {
    isEdit = false,
    franchiseChanges,
    accountFranchises = [],
    isAccountFranchisesError = false,
    isFetchingAccountFranchises = false
  } = props;

  const { accountEntity } = useOutletContext<{
    accountEntity: AccountEntityExtended;
  }>();

  const toast = useToaster();

  const [accountName, setAccountName] = useState(accountEntity.name);
  const { accountFavorites, toggleAccountFavorites } = useAccountFavourites();

  const {
    data: franchiseData = [],
    isFetching: isFetchingFranchises,
    isError: isFranchisesError
  } = useFranchiseList({
    enabled: isEdit && !isAccountFranchisesError
  });

  useEffect(() => {
    if (isEdit && isFranchisesError) {
      toast.toastError("Error loading franchises");
    }
  }, [isEdit && isFranchisesError]);

  const franchisesByName = useMemo(() => {
    if (!franchiseData?.length) return new Map();
    return new Map(
      franchiseData.map(f => [
        f.name,
        {
          id: f.id,
          name: f.name,
          abbreviation: f.abbreviation
        } as IFranchiseSelectorData
      ])
    );
  }, [franchiseData]);

  const availableFranchises = useMemo(
    () => Array.from(franchisesByName.values()),
    [franchisesByName]
  );

  const selectedFranchises = useMemo(() => {
    if (isEdit && franchiseChanges) {
      if (franchiseChanges.selectedFranchises.length === 0) return [];

      return franchiseChanges.selectedFranchises
        .map(name => franchisesByName.get(name))
        .filter(
          (franchise): franchise is IFranchiseSelectorData =>
            franchise !== undefined
        );
    }

    return accountFranchises.map(
      f =>
        ({
          id: f.id,
          name: f.name,
          abbreviation: f.abbreviation
        }) as IFranchiseSelectorData
    );
  }, [
    isEdit,
    franchiseChanges?.selectedFranchises,
    franchisesByName,
    accountFranchises
  ]);

  const setSelectedFranchises = useCallback(
    (franchises: IFranchiseSelectorData[]) => {
      franchiseChanges?.updateSelectedFranchises(franchises.map(f => f.name));
    },
    [franchiseChanges]
  );

  const dealerInfoCardHeader = (
    <CardHeader
      action={
        <>
          <Button
            analyticsConfiguration={{
              eventName: "Add/Remove Favorite"
            }}
            buttonStyle="aux"
            icon={
              <Tooltip
                position="bottom"
                size="large"
                toolTipContent={
                  accountFavorites?.includes(accountEntity.id)
                    ? "Remove favorite"
                    : "Add favorite"
                }
              >
                {accountFavorites?.includes(accountEntity.id) ? (
                  <HeartSolidIcon title="Remove favorite" />
                ) : (
                  <HeartIcon title="Add favorite" />
                )}
              </Tooltip>
            }
            onClick={() => {
              toggleAccountFavorites(accountEntity.id);
            }}
          />
        </>
      }
      title={<StyledCardTitleArea>Dealer Info</StyledCardTitleArea>}
    />
  );

  const franchiseElement = (
    <AccountFranchiseSelector
      availableFranchises={availableFranchises || []}
      isAccountFranchisesError={isAccountFranchisesError}
      isEdit={isEdit}
      isLoading={
        isEdit
          ? isFetchingFranchises || isFetchingAccountFranchises
          : isFetchingAccountFranchises
      }
      selectedFranchises={selectedFranchises}
      setSelectedFranchises={setSelectedFranchises}
    />
  );

  const dealerInfoCardContent = (
    <CardContent>
      <StyledUnorderedList>
        <PagesLandingPageListItem
          label="Name"
          type={isEdit ? "disabledInput" : undefined}
          value={accountEntity.name}
          onChange={e => setAccountName(e.target.value)}
        />
        <PagesLandingPageListItem
          label="id"
          type={isEdit ? "disabledInput" : undefined}
          value={accountEntity.id}
        />
        <PagesLandingPageListItem
          label="Status"
          type={isEdit ? "disabledInput" : undefined}
          value={accountEntity.status}
        />
        <PagesLandingPageListItem
          label="Url"
          type={isEdit ? "disabledInput" : undefined}
          value={addHttpsToUrlIfMissing(accountEntity.url)}
        />
        <PagesLandingPageListItem
          label="intUrl"
          type={isEdit ? "disabledInput" : "externalLink"}
          value={addHttpsToUrlIfMissing(accountEntity.internalUrl)}
        />
        <PagesLandingPageListItem
          label="Vtype"
          type={isEdit ? "disabledInput" : undefined}
          value={accountEntity.verticalType}
        />

        <PagesLandingPageListItem label="Franchises" value={franchiseElement} />
        <PagesLandingPageListItem
          label="SEO ID"
          type={isEdit ? "disabledInput" : undefined}
          value={accountEntity.seoId}
        />
        <PagesLandingPageListItem
          label="Call Tracking Campaigns"
          type={isEdit ? "disabledInput" : undefined}
          value={accountEntity.callTrackingCampaigns}
        />
        {!isEdit && (
          <Box
            display="flex"
            mt={2}
            sx={{
              flexWrap: "wrap"
            }}
            width="100%"
          >
            <Button
              analyticsConfiguration={{
                eventName: "open_influx_viewer"
              }}
              buttonStyle="secondary"
              size="small"
              sx={{ mr: 2 }}
              onClick={() => {
                window.open(
                  `https://influxtools.vpce.invsup.dealer.com/cgi-bin/feed_archives.cgi?action=viewDealer&dealerid=${accountEntity.id}`
                );
              }}
            >
              INFLUX VIEWER
            </Button>
            <Button
              analyticsConfiguration={{
                eventName: "open_export_viewer"
              }}
              buttonStyle="secondary"
              size="small"
              onClick={() => {
                window.open(
                  `https://influxtools.vpce.invsup.dealer.com/cgi-bin/export.cgi?action=viewExportConf&parentid=${accountEntity.id}`
                );
              }}
            >
              EXPORT VIEWER
            </Button>
          </Box>
        )}
      </StyledUnorderedList>
    </CardContent>
  );
  return (
    <Grid size={!isEdit ? { lg: 3, sm: 6, xs: 12 } : 12}>
      <StyledCard
        content={dealerInfoCardContent}
        header={dealerInfoCardHeader}
      />
    </Grid>
  );
}

const addHttpsToUrlIfMissing = (url: string): string => {
  if (url && !url.startsWith("http")) {
    return `https://${url}`;
  }
  return url;
};

export default DashboardDealerInfoCard;
